import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { api, errMsg } from "@/lib/api";
import { fmt, ddmmyyyy } from "@/lib/format";
import { monthLabel, monthRange } from "@/pages/SummaryPage";

const Month = ({ m, nav }) => (
  <section className="bg-white border border-slate-300 fade-up" data-testid={`reasons-month-${m.month}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b-2 border-slate-900">
      <h2 className="font-extrabold text-base md:text-lg tracking-tight">{monthLabel(m.month)}</h2>
      <div className="flex gap-1.5 text-[11px] font-bold">
        <span className="bg-red-100 text-red-800 px-2 py-0.5">{m.count} mismatches</span>
        <span className={`px-2 py-0.5 ${m.missing ? "bg-amber-200 text-amber-900" : "bg-green-100 text-green-800"}`} data-testid="reasons-missing-count">
          {m.missing ? `${m.missing} without reason` : "All explained"}
        </span>
        <span className="bg-slate-100 px-2 py-0.5 font-mono">Difference {fmt(m.pnl)}</span>
      </div>
    </div>
    <div className="ledger-scroll overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="text-[10px] uppercase tracking-[0.12em] text-slate-500 bg-slate-50">
          <tr>{["Date", "Book", "Difference", "Reason", "Note"].map((h) => <th key={h} className={`px-3 py-2 font-bold ${h === "Difference" ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {m.items.map((it) => (
            <tr key={it.entry_id} data-testid="reasons-row" className="border-t border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => nav(`/book/${it.book_id}?${it.sheet === "sett" ? "sheet=sett&" : ""}${monthRange(m.month)}`)}>
              <td className="px-3 py-2.5 font-mono whitespace-nowrap">{ddmmyyyy(it.date)}</td>
              <td className="px-3 py-2.5 whitespace-nowrap">
                <span className="font-bold uppercase">{it.book_name}</span>
                {it.sheet === "sett" && <span className="ml-1.5 text-[10px] font-bold bg-amber-200 px-1.5">NEW SETT</span>}
              </td>
              <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold text-red-700 whitespace-nowrap">{fmt(it.pnl)}</td>
              <td className="px-3 py-2.5" data-testid="reasons-row-reason">
                {it.reason || <span className="inline-flex items-center gap-1 text-amber-800 font-semibold text-xs"><AlertTriangle size={12} /> No reason</span>}
              </td>
              <td className="px-3 py-2.5 text-slate-600">{[it.note, it.diff].filter(Boolean).join(" · ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

export default function ReasonsPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [missingOnly, setMissingOnly] = useState(false);
  useEffect(() => { api.get("/reports/mismatches").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  const shown = (data || [])
    .map((m) => (missingOnly ? { ...m, items: m.items.filter((i) => !i.reason) } : m))
    .filter((m) => m.items.length);

  return (
    <div className="min-h-screen grid-paper">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Every Difference ≠ 0, month by month</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Reason report</h1>
          </div>
          <div className="flex border border-slate-300 bg-white">
            {[[false, "All mismatches"], [true, "Missing reason"]].map(([v, l]) => (
              <button key={l} data-testid={`reasons-filter-${v ? "missing" : "all"}`} onClick={() => setMissingOnly(v)}
                className={`h-10 px-3 text-xs font-semibold transition-colors ${missingOnly === v ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>{l}</button>
            ))}
          </div>
        </div>
        {!data ? <p className="text-sm text-slate-500" data-testid="reasons-loading">Loading…</p>
          : shown.length === 0 ? (
            <div className="border border-dashed border-slate-400 bg-white/60 p-10 text-center" data-testid="reasons-empty">
              <p className="font-semibold">{missingOnly ? "Every mismatch has a reason" : "No mismatches — all books balance"}</p>
            </div>
          ) : shown.map((m) => <Month key={m.month} m={m} nav={nav} />)}
      </main>
    </div>
  );
}
